/**
 * Phase 22 Plan 22-03 — WorkOrderCreateWizard (reshaped to 4 steps, D-229)
 *
 * Route: /work-orders/new
 * Permission gate: workorders.create
 *
 * 4-step Stepper:
 *   Step 1 — Client + Basics: Client dropdown + inline "+ New Client", WoNumber, Description, Dates
 *   Step 2 — Sites: multi-select active sites (≥1 required)
 *   Step 3 — Machines: multi-select from union of selected sites (≥1 required)
 *   Step 4 — KPIs: WizardAddKpiDialog flow (unchanged from Phase 17)
 */

import { useQuery } from '@tanstack/react-query';
import { useState, type JSX } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { CreateClientDialog } from '@/components/clients/CreateClientDialog';
import { KpiTemplatePickerDialog } from '@/components/kpis/KpiTemplatePickerDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { WizardAddKpiDialog } from '@/components/work-orders/WizardAddKpiDialog';
import { useClientsQuery } from '@/hooks/useClientQueries';
import { useCreateWorkOrderMutation } from '@/hooks/useWorkOrderQueries';
import { ApiError } from '@/lib/api/error';
import { useAuth } from '@/lib/auth/AuthContext';
import { useZodForm } from '@/lib/forms/useZodForm';
import { toast } from '@/lib/toast';
import { formatMachineName } from '@/lib/utils/machine-label';
import { createKpiRequest } from '@/services/kpis';
import { listMachinesRequest } from '@/services/machines';
import { listSitesRequest } from '@/services/sites';
import type { ClientDto } from '@/types/client';
import type { CreateKpiBody } from '@/types/kpi';
import type { KpiTemplate } from '@/types/kpi-template';
import type { MachineListItem } from '@/types/machines';
import type { PaginatedResult } from '@/types/roles';
import type { SiteListItem } from '@/types/sites';

// ---------------------------------------------------------------------------
// Step 1 — Client + Basics schema
// ---------------------------------------------------------------------------
const step1Schema = z
  .object({
    clientId: z.number().int().positive('Client is required'),
    woNumber: z
      .string()
      .regex(/^[A-Za-z0-9._-]{5,30}$/, 'WO number must be 5-30 alphanumeric chars')
      .optional()
      .or(z.literal('')),
    description: z.string().max(2000, 'Max 2000 characters').optional(),
    startDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'Start date is required (yyyy-MM-dd)'),
    endDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'End date is required (yyyy-MM-dd)'),
  })
  .refine((d) => d.endDate > d.startDate, {
    message: 'End date must be after start date',
    path: ['endDate'],
  });

type Step1Values = z.infer<typeof step1Schema>;

type WizardStep = 1 | 2 | 3 | 4;

export function WorkOrderCreateWizard(): JSX.Element {
  const { permissions } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<WizardStep>(1);
  const [step1Data, setStep1Data] = useState<Step1Values | null>(null);
  const [selectedSiteIds, setSelectedSiteIds] = useState<Set<number>>(new Set());
  const [selectedMachineIds, setSelectedMachineIds] = useState<Set<number>>(new Set());
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pendingKpis, setPendingKpis] = useState<CreateKpiBody[]>([]);
  const [addKpiOpen, setAddKpiOpen] = useState<boolean>(false);
  const [templatePickerOpen, setTemplatePickerOpen] = useState<boolean>(false);
  const [templateInitialValues, setTemplateInitialValues] = useState<
    Partial<CreateKpiBody> | undefined
  >(undefined);

  function handleTemplateSelected(template: KpiTemplate): void {
    setTemplateInitialValues({
      name: template.name,
      unit: template.unit,
      ...(template.customUnitText !== null ? { customUnitText: template.customUnitText } : {}),
      ...(template.description !== null ? { description: template.description } : {}),
    });
    setAddKpiOpen(true);
  }
  const [savingProgress, setSavingProgress] = useState<string | null>(null);
  const [createClientOpen, setCreateClientOpen] = useState<boolean>(false);

  const form = useZodForm({
    schema: step1Schema,
    defaultValues: {
      clientId: 0,
      woNumber: '',
      description: '',
      startDate: '',
      endDate: '',
    },
  });

  const clientId = form.watch('clientId');

  // Active clients for Step 1 dropdown
  const clientsQuery = useClientsQuery({ status: 'active', pageSize: 200 });

  // Active sites for Step 2
  const sitesQuery = useQuery<PaginatedResult<SiteListItem>>({
    queryKey: ['sites', 'active', 'forWoWizardStep2'],
    queryFn: () => listSitesRequest({ status: 'active', page: 1, pageSize: 200 }),
    enabled: step >= 2,
    staleTime: 30_000,
  });

  // Machines for Step 3 — fetch per each selected siteId then merge
  // For simplicity, use siteId param per site (backend accepts single siteId filter).
  // We issue up to one query per selected site and merge client-side.
  const siteIdsArray = [...selectedSiteIds];

  const machinesQuery = useQuery<PaginatedResult<MachineListItem>, ApiError>({
    queryKey: ['machines', 'active', 'forWoWizardStep3', siteIdsArray],
    queryFn: async () => {
      if (siteIdsArray.length === 0) return { items: [], page: 1, pageSize: 200, total: 0 };
      // If exactly one site, use siteId filter; for multiple, fetch without filter and
      // filter client-side (machines endpoint may not support multiple siteIds yet).
      if (siteIdsArray.length === 1) {
        const sid = siteIdsArray[0];
        if (sid === undefined) return { items: [], page: 1, pageSize: 200, total: 0 };
        return listMachinesRequest({ siteId: sid, status: 'active', pageSize: 200 });
      }
      // Multiple sites: fetch all active machines, filter to those whose siteId is in selection
      const all = await listMachinesRequest({ status: 'active', pageSize: 500 });
      const siteSet = new Set(siteIdsArray);
      return {
        ...all,
        items: all.items.filter((m) => m.siteId !== null && siteSet.has(m.siteId)),
      };
    },
    enabled: step >= 3 && siteIdsArray.length > 0,
    staleTime: 30_000,
  });

  const createMutation = useCreateWorkOrderMutation();

  if (!permissions.includes('workorders.create')) {
    return <Navigate to="/work-orders" replace />;
  }

  function toggleSite(id: number): void {
    setSelectedSiteIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function toggleMachine(id: number): void {
    setSelectedMachineIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  const onStep1Submit = form.handleSubmit((values) => {
    setStep1Data(values);
    setStep(2);
  });

  function handleSave(): void {
    if (step1Data === null) return;
    setErrorMessage(null);

    const siteIdsVal = [...selectedSiteIds];
    const siteIdsSchema = z.array(z.number()).min(1, 'Select at least one site');
    const siteResult = siteIdsSchema.safeParse(siteIdsVal);
    if (!siteResult.success) {
      setErrorMessage(siteResult.error.issues[0]?.message ?? 'Select at least one site');
      return;
    }

    const machineIdsArray = [...selectedMachineIds];
    const machineIdsSchema = z.array(z.number()).min(1, 'Select at least one machine');
    const machineResult = machineIdsSchema.safeParse(machineIdsArray);
    if (!machineResult.success) {
      setErrorMessage(machineResult.error.issues[0]?.message ?? 'Select at least one machine');
      return;
    }

    const createBody: import('@/types/work-orders').CreateWorkOrderRequest = {
      clientId: step1Data.clientId,
      siteIds: siteIdsVal,
      machineIds: machineIdsArray,
      startDate: step1Data.startDate,
      endDate: step1Data.endDate,
      ...(typeof step1Data.woNumber === 'string' && step1Data.woNumber.length > 0
        ? { woNumber: step1Data.woNumber }
        : {}),
      ...(typeof step1Data.description === 'string' && step1Data.description.length > 0
        ? { description: step1Data.description }
        : {}),
    };
    createMutation.mutate(
      createBody,
      {
        onSuccess: (wo) => {
          if (pendingKpis.length === 0) {
            toast.success(`Work Order ${wo.woNumber} created`);
            void navigate(`/work-orders/${String(wo.id)}`);
            return;
          }
          void (async () => {
            // REVIEW FF-013: previous implementation continued creating KPIs
            // after a failure, leaving the WO in a non-deterministic state. We
            // now stop on first failure, skip remaining KPIs, and surface a
            // clear "WO created — N of M KPIs added; remaining must be added
            // from the detail page" message. The WO itself is left intact so
            // the user can finish the setup on /work-orders/{id} without
            // re-doing the multi-step wizard.
            let succeeded = 0;
            let firstFailure: { name: string; reason: string } | null = null;
            let skipped = 0;
            for (let i = 0; i < pendingKpis.length; i += 1) {
              const k = pendingKpis[i];
              if (k === undefined) continue;
              if (firstFailure !== null) {
                skipped += 1;
                continue;
              }
              setSavingProgress(`Adding KPI ${String(i + 1)} of ${String(pendingKpis.length)}…`);
              try {
                await createKpiRequest(wo.id, k);
                succeeded += 1;
              } catch (err) {
                const msg = err instanceof ApiError
                  ? (err.detail ?? err.message)
                  : 'Unexpected error';
                firstFailure = { name: k.name, reason: msg };
              }
            }
            setSavingProgress(null);

            if (firstFailure === null) {
              toast.success(
                `Work Order ${wo.woNumber} created with ${String(succeeded)} KPI${succeeded === 1 ? '' : 's'}`,
              );
            } else {
              const detail =
                skipped > 0
                  ? `${succeeded} KPI(s) added before "${firstFailure.name}" failed: ${firstFailure.reason}. Remaining ${String(skipped)} KPI(s) skipped — add them from the Work Order detail page.`
                  : `${succeeded} KPI(s) added before "${firstFailure.name}" failed: ${firstFailure.reason}.`;
              toast.error(`Work Order created but KPI setup incomplete`, {
                description: detail,
              });
            }
            void navigate(`/work-orders/${String(wo.id)}`);
          })();
        },
        onError: (err) => {
          const msg = err.detail ?? err.message;
          setErrorMessage(msg);
          toast.error('Could not create work order', { description: msg });
        },
      },
    );
  }

  function handleAddKpi(body: CreateKpiBody): void {
    setPendingKpis((prev) => [...prev, body]);
  }

  function handleRemoveKpi(index: number): void {
    setPendingKpis((prev) => prev.filter((_, i) => i !== index));
  }

  function handleClientCreated(client: ClientDto): void {
    form.setValue('clientId', client.id, { shouldValidate: true });
    setCreateClientOpen(false);
  }

  const stepLabels: Record<WizardStep, string> = {
    1: 'Client & Basics',
    2: 'Sites',
    3: 'Machines',
    4: 'KPIs',
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">New Work Order</h1>
        <p className="text-muted-foreground">
          Step {step} of 4 — {stepLabels[step]}
        </p>
      </div>

      {/* Step progress indicator */}
      <div className="flex items-center gap-2" data-testid="wo-wizard-steps">
        {([1, 2, 3, 4] as WizardStep[]).map((s) => (
          <div
            key={s}
            className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium ${
              s === step
                ? 'bg-primary text-primary-foreground'
                : s < step
                  ? 'bg-primary/20 text-primary'
                  : 'bg-muted text-muted-foreground'
            }`}
            data-testid={`wo-wizard-step-indicator-${String(s)}`}
          >
            {s}
          </div>
        ))}
      </div>

      {/* Step 1 — Client + Basics */}
      {step === 1 && (
        <Card data-testid="wo-wizard-step-1">
          <CardHeader>
            <CardTitle>Client &amp; Basics</CardTitle>
            <CardDescription>
              Select the client and enter Work Order details. WO number is auto-generated if left blank.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onStep1Submit} noValidate className="space-y-4">
              {/* Client */}
              <div className="space-y-1">
                <Label htmlFor="wo-client">Client *</Label>
                <Select
                  value={clientId > 0 ? String(clientId) : ''}
                  onValueChange={(v) => {
                    const n = Number(v);
                    if (Number.isFinite(n) && n > 0) {
                      form.setValue('clientId', n, { shouldValidate: true });
                    }
                  }}
                  disabled={clientsQuery.isLoading}
                >
                  <SelectTrigger id="wo-client" data-testid="wo-wizard-client-trigger">
                    <SelectValue placeholder="Select client…" />
                  </SelectTrigger>
                  <SelectContent>
                    {(clientsQuery.data?.items ?? []).map((c) => (
                      <SelectItem
                        key={c.id}
                        value={String(c.id)}
                        data-testid={`wo-wizard-client-option-${String(c.id)}`}
                      >
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.formState.errors.clientId !== undefined && (
                  <p className="text-xs text-destructive">
                    {form.formState.errors.clientId.message}
                  </p>
                )}
                <button
                  type="button"
                  className="text-xs text-primary underline mt-1"
                  onClick={() => { setCreateClientOpen(true); }}
                  data-testid="wo-wizard-new-client-link"
                >
                  + New Client
                </button>
              </div>

              {/* WO Number */}
              <div className="space-y-1">
                <Label htmlFor="wo-number">WO Number (optional)</Label>
                <Input
                  id="wo-number"
                  placeholder="Auto-generated if blank"
                  data-testid="wo-wizard-wonumber-input"
                  {...form.register('woNumber')}
                />
                {form.formState.errors.woNumber !== undefined && (
                  <p className="text-xs text-destructive">
                    {form.formState.errors.woNumber.message}
                  </p>
                )}
              </div>

              {/* Description */}
              <div className="space-y-1">
                <Label htmlFor="wo-description">Description</Label>
                <Textarea
                  id="wo-description"
                  placeholder="Optional description…"
                  rows={3}
                  data-testid="wo-wizard-description-input"
                  {...form.register('description')}
                />
                {form.formState.errors.description !== undefined && (
                  <p className="text-xs text-destructive">
                    {form.formState.errors.description.message}
                  </p>
                )}
              </div>

              {/* Dates */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor="wo-start-date">Start Date *</Label>
                  <Input
                    id="wo-start-date"
                    type="date"
                    data-testid="wo-wizard-start-date-input"
                    {...form.register('startDate')}
                  />
                  {form.formState.errors.startDate !== undefined && (
                    <p className="text-xs text-destructive">
                      {form.formState.errors.startDate.message}
                    </p>
                  )}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="wo-end-date">End Date *</Label>
                  <Input
                    id="wo-end-date"
                    type="date"
                    data-testid="wo-wizard-end-date-input"
                    {...form.register('endDate')}
                  />
                  {form.formState.errors.endDate !== undefined && (
                    <p className="text-xs text-destructive" data-testid="wo-wizard-enddate-error">
                      {form.formState.errors.endDate.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="pt-2">
                <Button type="submit" data-testid="wo-wizard-step1-next">
                  Next
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Step 2 — Sites */}
      {step === 2 && (
        <Card data-testid="wo-wizard-step-2">
          <CardHeader>
            <CardTitle>Select Sites</CardTitle>
            <CardDescription>
              Choose at least one site to include in this Work Order.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {sitesQuery.isLoading && (
              <p className="text-sm text-muted-foreground" data-testid="wo-wizard-sites-loading">
                Loading sites…
              </p>
            )}
            {sitesQuery.isError && (
              <p className="text-sm text-destructive">Failed to load sites.</p>
            )}
            {(sitesQuery.data?.items ?? []).length === 0 && sitesQuery.isSuccess && (
              <p className="text-sm text-muted-foreground">No active sites found.</p>
            )}
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {(sitesQuery.data?.items ?? []).map((s) => (
                <div key={s.id} className="flex items-center gap-3">
                  <Checkbox
                    id={`wo-site-${String(s.id)}`}
                    checked={selectedSiteIds.has(s.id)}
                    onCheckedChange={() => { toggleSite(s.id); }}
                    data-testid={`wo-wizard-site-checkbox-${String(s.id)}`}
                  />
                  <Label
                    htmlFor={`wo-site-${String(s.id)}`}
                    className="font-normal cursor-pointer"
                  >
                    {s.name}
                  </Label>
                </div>
              ))}
            </div>
            {selectedSiteIds.size === 0 && sitesQuery.isSuccess && (
              <p className="text-xs text-muted-foreground" data-testid="wo-wizard-sites-required">
                Select at least one site to continue.
              </p>
            )}
            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setStep(1); }}
                data-testid="wo-wizard-step2-back"
              >
                Back
              </Button>
              <Button
                type="button"
                onClick={() => { setStep(3); }}
                disabled={selectedSiteIds.size === 0}
                data-testid="wo-wizard-step2-next"
              >
                Next
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3 — Machines */}
      {step === 3 && (
        <Card data-testid="wo-wizard-step-3">
          <CardHeader>
            <CardTitle>Select Machines</CardTitle>
            <CardDescription>
              Choose at least one machine from the selected sites.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {machinesQuery.isLoading && (
              <p className="text-sm text-muted-foreground" data-testid="wo-wizard-machines-loading">
                Loading machines…
              </p>
            )}
            {machinesQuery.isError && (
              <p className="text-sm text-destructive">Failed to load machines.</p>
            )}
            {(machinesQuery.data?.items ?? []).length === 0 && machinesQuery.isSuccess && (
              <p className="text-sm text-muted-foreground">
                No active machines at selected sites.
              </p>
            )}
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {(machinesQuery.data?.items ?? []).map((m) => (
                <div key={m.id} className="flex items-center gap-3">
                  <Checkbox
                    id={`wo-machine-${String(m.id)}`}
                    checked={selectedMachineIds.has(m.id)}
                    onCheckedChange={() => { toggleMachine(m.id); }}
                    data-testid={`wo-wizard-machine-checkbox-${String(m.id)}`}
                  />
                  <Label
                    htmlFor={`wo-machine-${String(m.id)}`}
                    className="font-normal cursor-pointer"
                  >
                    {formatMachineName(m.name, m.vehicleNumber, m.code)}
                    <span className="ml-2 text-xs text-muted-foreground">
                      ({m.machineTypeName})
                    </span>
                  </Label>
                </div>
              ))}
            </div>
            {selectedMachineIds.size === 0 && machinesQuery.isSuccess && (
              <p className="text-xs text-muted-foreground" data-testid="wo-wizard-machines-required">
                Select at least one machine to continue.
              </p>
            )}
            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setStep(2); }}
                data-testid="wo-wizard-step3-back"
              >
                Back
              </Button>
              <Button
                type="button"
                onClick={() => { setStep(4); }}
                disabled={selectedMachineIds.size === 0}
                data-testid="wo-wizard-step3-next"
              >
                Next
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4 — KPIs */}
      {step === 4 && (() => {
        const wizardMachines = (machinesQuery.data?.items ?? [])
          .filter((m) => selectedMachineIds.has(m.id))
          .map((m) => ({
            machineId: m.id,
            machineCode: m.code,
            machineName: m.name,
            vehicleNumber: m.vehicleNumber,
          }));
        const isSaving = createMutation.isPending || savingProgress !== null;

        return (
          <Card data-testid="wo-wizard-step-4">
            <CardHeader>
              <CardTitle>KPIs</CardTitle>
              <CardDescription>
                Add KPIs to this Work Order. You can also save now and add KPIs later.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {pendingKpis.length === 0 && (
                <div
                  className="rounded-md border border-border bg-muted/50 px-4 py-3 text-sm"
                  data-testid="wo-wizard-kpi-empty"
                >
                  <p className="text-muted-foreground">
                    No KPIs added yet. Click <strong>Add KPI</strong> below, or save and add from the detail page.
                  </p>
                </div>
              )}

              {pendingKpis.length > 0 && (
                <ul
                  className="divide-y divide-border rounded-md border border-border"
                  data-testid="wo-wizard-kpi-list"
                >
                  {pendingKpis.map((k, i) => (
                    <li
                      key={`${k.name}-${String(i)}`}
                      className="flex items-start justify-between gap-3 px-4 py-3"
                      data-testid={`wo-wizard-kpi-row-${String(i)}`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium">{k.name}</p>
                        <p className="text-xs text-muted-foreground">
                          Unit: {k.unit === 'Custom' && k.customUnitText
                            ? k.customUnitText
                            : k.unit}
                          {k.targetPerShift !== undefined && (
                            <> · Target/shift: {String(k.targetPerShift)}</>
                          )}
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => { handleRemoveKpi(i); }}
                        disabled={isSaving}
                        data-testid={`wo-wizard-kpi-remove-${String(i)}`}
                      >
                        Remove
                      </Button>
                    </li>
                  ))}
                </ul>
              )}

              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setTemplatePickerOpen(true); }}
                  disabled={isSaving || wizardMachines.length === 0}
                  data-testid="wo-wizard-kpi-use-template-button"
                >
                  Use Template
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setTemplateInitialValues(undefined);
                    setAddKpiOpen(true);
                  }}
                  disabled={isSaving || wizardMachines.length === 0}
                  data-testid="wo-wizard-kpi-add-button"
                >
                  + Add KPI
                </Button>
              </div>

              {savingProgress !== null && (
                <div
                  className="rounded-md border border-border bg-muted/50 px-3 py-2 text-sm"
                  data-testid="wo-wizard-save-progress"
                >
                  {savingProgress}
                </div>
              )}

              {errorMessage !== null && (
                <div
                  className="rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-sm text-destructive"
                  data-testid="wo-wizard-save-error"
                >
                  {errorMessage}
                </div>
              )}

              <div className="flex flex-col gap-2 pt-2 sm:flex-row">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setStep(3); }}
                  disabled={isSaving}
                  data-testid="wo-wizard-step4-back"
                >
                  Back
                </Button>
                <Button
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving}
                  data-testid="wo-wizard-step4-save"
                >
                  {isSaving
                    ? 'Saving…'
                    : pendingKpis.length === 0
                      ? 'Save (no KPIs)'
                      : `Save with ${String(pendingKpis.length)} KPI${pendingKpis.length === 1 ? '' : 's'}`}
                </Button>
              </div>
            </CardContent>

            {addKpiOpen && (
              <WizardAddKpiDialog
                open={addKpiOpen}
                onOpenChange={(o) => {
                  if (!o) setTemplateInitialValues(undefined);
                  setAddKpiOpen(o);
                }}
                machines={wizardMachines}
                onAdd={handleAddKpi}
                initialValues={templateInitialValues}
              />
            )}

            {templatePickerOpen && (
              <KpiTemplatePickerDialog
                open={templatePickerOpen}
                onOpenChange={setTemplatePickerOpen}
                onSelect={handleTemplateSelected}
              />
            )}
          </Card>
        );
      })()}

      {/* Inline Create Client dialog */}
      <CreateClientDialog
        open={createClientOpen}
        onOpenChange={setCreateClientOpen}
        onCreated={handleClientCreated}
      />
    </div>
  );
}
